import React, { useContext, useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Menu, Transition, Disclosure } from '@headlessui/react'
import { ChevronDown, Menu as MenuIcon, X } from 'lucide-react'
import { isDev, capitalizeFirstLetter } from '../utils'
import NavTranslation from './NavTranslation'
import "./Nav.css"
import darkModeContext from '../contexts/DarkModeContext'

const Nav = () => {
    const { t, i18n } = useTranslation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { darkMode } = useContext(darkModeContext);

    const isSignupPage = location.pathname.includes('signup');

    return (
        <>
            {/* Top Bar for Translation (Dev only) */}
            {isDev && (
                <div className=' relative z-[51] dark:bg-neutral-950 flex justify-end pr-3 py-1' style={{ zIndex: 1000 }}>
                    <NavTranslation />
                </div>
            )}

            <nav className="sticky top-0 z-40 w-full border-y border-neutral-100 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:bg-neutral-950/95 dark:border-neutral-800 dark:text-white px-4 md:px-6 lg:pr-10 lg:mr-20">
                <div className="flex h-20 items-center justify-between">

                    {/* Logo */}
                    <Link className="flex items-center gap-2" to="/">
                        <img
                            src="https://www.mlg-consulting.com/manager_cc/docs/archives/241001101410_mgv-logo-200.png"
                            alt="My Global Village"
                            className={`${!darkMode && 'invert-custom'} w-52 h-auto object-contain`}
                        />
                    </Link>

                    {!isSignupPage && (
                        <>
                            {/* Mobile Menu Button */}
                            <div className="flex md:hidden">
                                <button
                                    type="button"
                                    className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-white"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                >
                                    <span className="sr-only">Open main menu</span>
                                    {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <MenuIcon className="h-6 w-6" aria-hidden="true" />}
                                </button>
                            </div>

                            {/* Desktop Navigation */}
                            <div className="hidden md:flex md:gap-x-6 items-center">

                                {/* Dropdown: A Propos */}
                                <Menu as="div" className="relative">
                                    <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                        {capitalizeFirstLetter(t('event_nav.a_propos'))}
                                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                    </Menu.Button>
                                    <NavTransition>
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md  dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            <div className="py-1">
                                                <MenuItemLink to="/whatis" hidden>{t('nav.whatis')}</MenuItemLink>
                                                <MenuItemLink to="/connected-studios" hidden>{t('nav.studios')}</MenuItemLink>
                                                <MenuItemLink to="/commite-organisation" hidden>{t('nav.commite_orga')}</MenuItemLink>
                                                <MenuItemLink to="/map" hidden>{t('nav.map')}</MenuItemLink>
                                                <MenuItemLink to="/whois">{t('nav.whois')}</MenuItemLink>
                                                <MenuItemLink to="/leadership-team" hidden>Notre Leadership Team</MenuItemLink>
                                            </div>
                                        </Menu.Items>
                                    </NavTransition>
                                </Menu>

                                {/* Dropdown: Nos domaines d'intervention */}
                                <Menu as="div" className="relative">
                                    <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                        {capitalizeFirstLetter(t('nav.domaines_interventions'))}
                                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                    </Menu.Button>
                                    <NavTransition>
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md  dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            <div className="py-1">
                                                <MenuItemLink to="/calendar">{t('nav.agenda')}</MenuItemLink>
                                                <MenuItemLink to="/collectif-categorie/collectif_planaction">{t('nav.action_plans')}</MenuItemLink>
                                                <MenuItemLink className="hidden" to="/leadership-team">Nos solutions événémentielles</MenuItemLink>
                                                <MenuItemLink to="/leadership-team" hidden>Notre réseau, notre communauté</MenuItemLink>
                                            </div>
                                        </Menu.Items>
                                    </NavTransition>
                                </Menu>

                                {/* Dropdown: Agenda (Hidden in original) */}
                                <Menu as="div" className="relative hidden">
                                    <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                        {capitalizeFirstLetter(t('nav.agenda'))}
                                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                    </Menu.Button>
                                    <NavTransition>
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md   dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            <div className="py-1">
                                                <MenuItemLink to="/leading-events" hidden>Evénéments leaders</MenuItemLink>
                                                <MenuItemLink to="/calendar">Agenda annuel</MenuItemLink>
                                                <MenuItemLink to="/pre-selection-calendar" hidden>Pré-qualifications sessions de pitch</MenuItemLink>
                                                <MenuItemLink to="/affiliated-events" hidden>Evénements affiliés</MenuItemLink>
                                            </div>
                                        </Menu.Items>
                                    </NavTransition>
                                </Menu>

                                {/* Dropdown: Annuaire Collectifs */}
                                <Menu as="div" className="relative">
                                    <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                        {capitalizeFirstLetter(t('nav.annuaire_collectifs'))}
                                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                    </Menu.Button>
                                    <NavTransition>
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md  dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            <div className="py-1">
                                                <MenuItemLink to="/collectif-categorie/collectif_sectoriel" hidden>{t('nav.collectifs_sectoriels')}</MenuItemLink>
                                                <MenuItemLink to="/collectif-categorie/collectif_geographique">{t('nav.collectifs_geographiques')}</MenuItemLink>
                                                <MenuItemLink to="/collectif-categorie/collectif_technologique" hidden>{t('nav.collectifs_technologiques')}</MenuItemLink>
                                                <MenuItemLink to="/collectif-categorie/collectif_planaction">{t('nav.collectifs_planaction')}</MenuItemLink>
                                                <MenuItemLink to="/collectif-categorie/collectif_partenaire">{t('nav.collectifs_cerclepartenaires')}</MenuItemLink>
                                                <MenuItemLink to="#" hidden>{t('nav.tetes_affiche')}</MenuItemLink>
                                                <MenuItemLink to="#" hidden>Annuaire des coordinateurs</MenuItemLink>
                                                <MenuItemLink to="/programmes-innovation" hidden>{t('nav.programmes')}</MenuItemLink>
                                                <MenuItemLink to="#" hidden>Annuaire des lauréats</MenuItemLink>
                                            </div>
                                        </Menu.Items>
                                    </NavTransition>
                                </Menu>

                                {/* Dropdown: Presse (Hidden in original) */}
                                <Menu as="div" className="relative hidden">
                                    <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                        {capitalizeFirstLetter("PRESSE")}
                                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                    </Menu.Button>
                                    <NavTransition>
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md   dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            <div className="py-1">
                                                <MenuItemLink to="/press">{t('event_nav.espace_presse')}</MenuItemLink>
                                                <MenuItemLink to="/press-partenaire">{t('event_nav.partenaires_presse')}</MenuItemLink>
                                            </div>
                                        </Menu.Items>
                                    </NavTransition>
                                </Menu>

                                <Link className="hidden text-sm font-semibold leading-6 text-white bg-pink-600 px-3 py-1 rounded-full hover:bg-pink-500 transition-colors" to={`/programm/webcasts`}> Webcasts </Link>

                            </div>
                        </>
                    )}
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <div className={`fixed inset-0 z-50 transform transition-transform duration-300 md:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Backdrop */}
                <div className="fixed inset-0 bg-black/25" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />

                {/* Panel */}
                <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white dark:bg-neutral-950 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="-m-1.5 p-1.5">
                            <span className="sr-only">My Global Village</span>
                            <img
                                className="h-8 w-auto"
                                src="https://www.mlg-consulting.com/manager_cc/docs/archives/241001101410_mgv-logo-200.png"
                                alt=""
                            />
                        </Link>
                        <button
                            type="button"
                            className="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-white"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <span className="sr-only">Close menu</span>
                            <X className="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="mt-6 flow-root">
                        <div className="-my-6 divide-y divide-gray-500/10">
                            <div className="space-y-2 py-6">

                                <Disclosure as="div" className="-mx-3">
                                    {({ open }) => (
                                        <>
                                            <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                {capitalizeFirstLetter(t('event_nav.a_propos'))}
                                                <ChevronDown className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
                                            </Disclosure.Button>
                                            <Disclosure.Panel className="mt-2 space-y-2">
                                                <MobileMenuItemLink to="/whatis" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.whatis')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/connected-studios" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.studios')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/commite-organisation" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.commite_orga')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/map" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.map')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/whois" setMobileMenuOpen={setMobileMenuOpen}>{t('nav.whois')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/leadership-team" hidden setMobileMenuOpen={setMobileMenuOpen}>Notre Leadership Team</MobileMenuItemLink>
                                            </Disclosure.Panel>
                                        </>
                                    )}
                                </Disclosure>

                                <Disclosure as="div" className="-mx-3">
                                    {({ open }) => (
                                        <>
                                            <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                {capitalizeFirstLetter(t('nav.domaines_interventions'))}
                                                <ChevronDown className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
                                            </Disclosure.Button>
                                            <Disclosure.Panel className="mt-2 space-y-2">
                                                <MobileMenuItemLink to="/calendar" setMobileMenuOpen={setMobileMenuOpen}>{t('nav.agenda')} </MobileMenuItemLink>
                                                <MobileMenuItemLink to="/collectif-categorie/collectif_planaction" setMobileMenuOpen={setMobileMenuOpen}>{t('nav.action_plans')} </MobileMenuItemLink>
                                                <MobileMenuItemLink className="hidden" to="/leadership-team" setMobileMenuOpen={setMobileMenuOpen}>Nos solutions événémentielles</MobileMenuItemLink>
                                                <MobileMenuItemLink className="hidden" to="/leadership-team" setMobileMenuOpen={setMobileMenuOpen}>Notre réseau, notre communauté</MobileMenuItemLink>
                                            </Disclosure.Panel>
                                        </>
                                    )}
                                </Disclosure>

                                <Disclosure as="div" className="-mx-3">
                                    {({ open }) => (
                                        <>
                                            <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                {capitalizeFirstLetter(t('nav.annuaire_collectifs'))}
                                                <ChevronDown className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
                                            </Disclosure.Button>
                                            <Disclosure.Panel className="mt-2 space-y-2">
                                                <MobileMenuItemLink to="/collectif-categorie/collectif_sectoriel" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.collectifs_sectoriels')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/collectif-categorie/collectif_geographique" setMobileMenuOpen={setMobileMenuOpen}>{t('nav.collectifs_geographiques')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/collectif-categorie/collectif_technologique" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.collectifs_technologiques')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/collectif-categorie/collectif_planaction" setMobileMenuOpen={setMobileMenuOpen}>{t('nav.collectifs_planaction')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/collectif-categorie/collectif_partenaire" setMobileMenuOpen={setMobileMenuOpen}>{t('nav.collectifs_cerclepartenaires')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="#" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.tetes_affiche')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="#" hidden setMobileMenuOpen={setMobileMenuOpen}>Annuaire des coordinateurs</MobileMenuItemLink>
                                                <MobileMenuItemLink to="/programmes-innovation" hidden setMobileMenuOpen={setMobileMenuOpen}>{t('nav.programmes')}</MobileMenuItemLink>
                                                <MobileMenuItemLink to="#" hidden setMobileMenuOpen={setMobileMenuOpen}>Annuaire des lauréats</MobileMenuItemLink>
                                            </Disclosure.Panel>
                                        </>
                                    )}
                                </Disclosure>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

// Helper Components
const NavTransition = ({ children }) => (
    <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
    >
        {children}
    </Transition>
)

const MenuItemLink = ({ to, children, hidden = false, ...props }) => {
    if (hidden) return null;
    return (
        <Menu.Item>
            {({ active }) => (
                <Link
                    to={to}
                    className={`
                        ${active ? 'bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}
                        block px-4 py-2 text-sm
                    `}
                    {...props}
                >
                    {capitalizeFirstLetter(children)}
                </Link>
            )}
        </Menu.Item>
    )
}

const MobileMenuItemLink = ({ to, children, hidden = false, setMobileMenuOpen, ...props }) => {
    if (hidden) return null;
    return (
        <Link
            to={to}
            className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-900"
            onClick={() => setMobileMenuOpen(false)}
            {...props}
        >
            {capitalizeFirstLetter(children)}
        </Link>
    )
}

export default Nav;